import React from 'react';
import axios from 'axios';
import './AIAssistant.css';

const API_URL = process.env.REACT_APP_API_URL || 
  (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5001');

interface AIAssistantProps {
  context?: 'goals' | 'tasks' | 'planner' | 'analytics';
  onDataChanged?: () => void | Promise<void>; // Callback когда AI изменил данные
  currentPage?: string; // Текущая страница приложения
}

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
}

const AIAssistant: React.FC<AIAssistantProps> = ({ context = 'tasks', onDataChanged, currentPage }) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [messages, setMessages] = React.useState<ChatMessage[]>([]);
  const [inputText, setInputText] = React.useState('');
  const [isSending, setIsSending] = React.useState(false);
  const chatEndRef = React.useRef<HTMLDivElement>(null);
  
  // Получаем userId для разделения истории между пользователями
  const getUserId = () => {
    const userData = localStorage.getItem('user');
    if (userData) {
      try {
        const user = JSON.parse(userData);
        return user.id || 'guest';
      } catch {
        return 'guest';
      }
    }
    return 'guest';
  };

  // Проверяем доступность AI на текущей странице
  const isAIAvailable = currentPage === '/goals' || !currentPage;

  // Позиция и размер окна
  const [windowPosition, setWindowPosition] = React.useState(() => {
    const saved = localStorage.getItem('ai_window_position');
    return saved ? JSON.parse(saved) : { x: window.innerWidth / 2 - 300, y: window.innerHeight / 2 - 250 };
  });
  const [windowSize, setWindowSize] = React.useState(() => {
    const saved = localStorage.getItem('ai_window_size');
    return saved ? JSON.parse(saved) : { width: 600, height: 500 };
  });

  // Состояние перетаскивания окна
  const [isDragging, setIsDragging] = React.useState(false);
  const [isResizing, setIsResizing] = React.useState(false);
  const [dragStart, setDragStart] = React.useState({ x: 0, y: 0 });

  // Сохраняем позицию и размер окна
  React.useEffect(() => {
    localStorage.setItem('ai_window_position', JSON.stringify(windowPosition));
  }, [windowPosition]);

  React.useEffect(() => {
    localStorage.setItem('ai_window_size', JSON.stringify(windowSize));
  }, [windowSize]);

  // Слушаем клики на кнопку в хедере
  React.useEffect(() => {
    const handleHeaderButtonClick = () => {
      setIsOpen(true);
    };

    const button = document.getElementById('ai-assistant-header-button');
    if (button) {
      button.addEventListener('click', handleHeaderButtonClick);
      return () => button.removeEventListener('click', handleHeaderButtonClick);
    }
  }, []);

  // Auto-scroll to bottom
  React.useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Загружаем историю из localStorage при открытии
  React.useEffect(() => {
    if (isOpen) {
      const userId = getUserId();
      const saved = localStorage.getItem(`ai_assistant_history_${userId}`);
      if (saved) {
        try {
          setMessages(JSON.parse(saved));
        } catch (err) {
          console.error('Failed to load chat history:', err);
        }
      }
    }
  }, [isOpen]);

  // Обработка перетаскивания окна
  const handleMouseDownWindow = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('.ai-assistant-header')) {
      setIsDragging(true);
      setDragStart({ x: e.clientX - windowPosition.x, y: e.clientY - windowPosition.y });
    }
  };

  const handleMouseDownResize = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsResizing(true);
    setDragStart({ x: e.clientX, y: e.clientY });
  };

  React.useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging) {
        setWindowPosition({
          x: Math.max(0, Math.min(window.innerWidth - 200, e.clientX - dragStart.x)),
          y: Math.max(0, Math.min(window.innerHeight - 100, e.clientY - dragStart.y))
        });
      } else if (isResizing) {
        const deltaX = e.clientX - dragStart.x;
        const deltaY = e.clientY - dragStart.y;
        setWindowSize({
          width: Math.max(400, Math.min(1200, windowSize.width + deltaX)),
          height: Math.max(300, Math.min(900, windowSize.height + deltaY))
        });
        setDragStart({ x: e.clientX, y: e.clientY });
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      setIsResizing(false);
    };

    if (isDragging || isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, isResizing, dragStart, windowPosition, windowSize]);



  // Сохраняем историю
  React.useEffect(() => {
    if (messages.length > 0) {
      const userId = getUserId();
      localStorage.setItem(`ai_assistant_history_${userId}`, JSON.stringify(messages));
    }
  }, [messages]);

  const handleSend = async () => {
    if (!inputText.trim() || isSending) return;

    const userMessage: ChatMessage = {
      role: 'user',
      content: inputText.trim(),
      timestamp: new Date().toISOString(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputText('');
    setIsSending(true);

    try {
      const token = localStorage.getItem('token');
      const messageText = userMessage.content.toLowerCase();

      // ========================================
      // 🎯 УНИВЕРСАЛЬНЫЙ МАРШРУТИЗАТОР
      // Отправляем запрос в единый AI endpoint, который сам решит что делать
      // ========================================

      
      // Отправляем всё в единый endpoint - AI сам определит намерение
      const response = await axios.post(
        `${API_URL}/api/ai/universal`,
        { 
          message: userMessage.content,
          chatHistory: messages
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Обрабатываем ответ
      let aiMessageContent = response.data.message || response.data.response || 'Готово!';
      
      // Сохраняем метаданные если есть
      if (response.data.createdTask) {
        aiMessageContent += ` [ID задачи: ${response.data.createdTask.id}]`;
      }
      if (response.data.createdGoal) {
        aiMessageContent += ` [ID цели: ${response.data.createdGoal.id}]`;
      }

      const aiMessage: ChatMessage = {
        role: 'assistant',
        content: aiMessageContent,
        timestamp: new Date().toISOString(),
      };

      setMessages((prev) => [...prev, aiMessage]);

      // Если AI выполнил действия - обновляем данные
      if (response.data.actionExecuted || response.data.needsRefresh) {
        
        if (onDataChanged) {
          await onDataChanged();
        }
        
        // Отправляем события обновления
        if (response.data.entityType === 'goal' || response.data.entityType === 'goals') {
          window.dispatchEvent(new CustomEvent('ai-goals-updated'));
        } else if (response.data.entityType === 'task' || response.data.entityType === 'tasks') {
          window.dispatchEvent(new CustomEvent('ai-tasks-updated'));
        }
      }


    } catch (err: any) {
      console.error('AI Assistant error:', err);
      const errorMessage: ChatMessage = {
        role: 'assistant',
        content: err.response?.data?.message || err.response?.data?.error || 'Произошла ошибка. Попробуйте ещё раз.',
        timestamp: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const clearHistory = async () => {
    if (messages.length === 0) return;

    const confirmClear = window.confirm('Очистить историю чата? Я сохраню краткую память о разговоре.');
    if (!confirmClear) return;

    try {
      const token = localStorage.getItem('token');
      
      // Запрашиваем у AI сжатое резюме истории
      const response = await axios.post(
        `${API_URL}/api/ai/compress-history`,
        { chatHistory: messages },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const summary = response.data.summary;
      
      // Очищаем историю, но оставляем системное сообщение с памятью
      const memoryMessage: ChatMessage = {
        role: 'assistant',
        content: `💭 Память о предыдущем разговоре: ${summary}`,
        timestamp: new Date().toISOString(),
      };

      setMessages([memoryMessage]);
      const userId = getUserId();
      localStorage.setItem(`ai_assistant_history_${userId}`, JSON.stringify([memoryMessage]));
    } catch (err) {
      console.error('Failed to compress history:', err);
      // Просто очищаем если не получилось сжать
      setMessages([]);
      const userId = getUserId();
      localStorage.removeItem(`ai_assistant_history_${userId}`);
    }
  };

  return (
    <>

      {/* Плавающее окно */}
      {isOpen && (
        <div
          className="ai-assistant-floating-window"
          style={{
            left: `${windowPosition.x}px`,
            top: `${windowPosition.y}px`,
            width: `${windowSize.width}px`,
            height: `${windowSize.height}px`,
            cursor: isDragging ? 'grabbing' : 'default'
          }}
          onMouseDown={handleMouseDownWindow}
        >
          {/* Header - draggable */}
          <div className="ai-assistant-header" style={{ cursor: 'grab' }}>
              <div className="ai-assistant-title">
                <span className="ai-icon">🤖</span>
                <h3>AI Помощник</h3>
              </div>
              <div className="ai-assistant-actions">
                <button className="clear-btn" onClick={clearHistory} title="Очистить историю">
                  🗑️
                </button>
                <button className="clear-btn" onClick={() => setIsOpen(false)} title="Закрыть">
                  ✕
                </button>
              </div>
            </div>

        {/* Context indicator */}
        <div className="ai-assistant-context">
          {context === 'goals' && '📌 Контекст: Цели'}
          {context === 'tasks' && '📋 Контекст: Дела'}
          {context === 'planner' && '🤖 Контекст: Планировщик'}
          {context === 'analytics' && '📊 Контекст: Аналитика'}
        </div>

        {/* Messages */}
        <div className="ai-assistant-messages">
          {!isAIAvailable ? (
            <div className="ai-assistant-welcome">
              <div className="welcome-icon">🚧</div>
              <h4>AI Помощник пока недоступен</h4>
              <p style={{ fontSize: '16px', lineHeight: '1.6', color: '#666' }}>
                Сейчас помощник работает только с <strong>целями и задачами</strong>.
              </p>
              <p style={{ fontSize: '15px', marginTop: '20px' }}>
                Перейдите в раздел <strong style={{ color: '#667eea' }}>🎯 Цели и Дела</strong>, чтобы использовать AI помощника.
              </p>
              <div style={{ marginTop: '30px', padding: '15px', background: '#f0f4ff', borderRadius: '8px', fontSize: '14px', color: '#555' }}>
                💡 <strong>Скоро:</strong> AI помощник научится работать с финансами, здоровьем и семьёй!
              </div>
            </div>
          ) : messages.length === 0 ? (
            <div className="ai-assistant-welcome">
              <div className="welcome-icon">👋</div>
              <h4>Привет! Я твой AI-помощник</h4>
              <p>Я могу:</p>
              <ul>
                <li>✨ Создавать новые задачи</li>
                <li>✏️ Редактировать существующие</li>
                <li>📅 Ставить задачи на сегодня</li>
                <li>✅ Отмечать выполненные</li>
                <li>🗑️ Удалять ненужные</li>
              </ul>
              <p className="hint">Просто напиши что нужно сделать!</p>
            </div>
          ) : (
            messages.map((msg, idx) => (
              <div key={idx} className={`message ${msg.role}`}>
                <div className="message-avatar">{msg.role === 'user' ? '👤' : '🤖'}</div>
                <div className="message-content">
                  <div className="message-text">{msg.content}</div>
                  <div className="message-time">
                    {new Date(msg.timestamp).toLocaleTimeString('ru-RU', {
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </div>
                </div>
              </div>
            ))
          )}
          <div ref={chatEndRef} />
        </div>

        {/* Input - только если AI доступен */}
        {isAIAvailable && (
          <>
            <div className="ai-assistant-input">
              <textarea
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Напиши что нужно сделать..."
                rows={2}
                disabled={isSending}
              />
              <button onClick={handleSend} disabled={!inputText.trim() || isSending} className="send-btn">
                {isSending ? '⏳' : '📤'}
              </button>
            </div>

            {/* Examples */}
            {messages.length === 0 && (
              <div className="ai-assistant-examples">
                <div className="example-title">Примеры команд:</div>
                <div className="example-items">
                  <button onClick={() => setInputText('создай задачу: позвонить маме')}>
                    создай задачу: позвонить маме
                  </button>
                  <button onClick={() => setInputText('поставь все задачи Лмеда на сегодня')}>
                    поставь все задачи Лмеда на сегодня
                  </button>
                  <button onClick={() => setInputText('покажи что есть по проекту винтикет')}>
                    покажи что есть по проекту винтикет
                  </button>
                </div>
              </div>
            )}
          </>
        )}

          {/* Resize handle */}
          <div 
            className="ai-assistant-resize-handle"
            onMouseDown={handleMouseDownResize}
            title="Изменить размер"
          />
        </div>
      )}
    </>
  );
};

export default AIAssistant;
